import { useQuery } from '@tanstack/react-query';
import { FileText, Search } from 'lucide-react';
import { useState } from 'react';
import { accountApi } from '@/api/commercial/accounts';
import { creditAnalysisApi } from '@/api/commercial/credit-analysis';
import { CreditAnalysisHistoryList } from '@/components/commercial/credit-analysis/credit-analysis-history-list';
import { Card, CardContent } from '@/components/ui/card';
import type { CreditAnalysisTabProps } from './general-tab';

export function CreditAnalysisDocumentsTab({ creditAnalysis }: CreditAnalysisTabProps) {
  const [page, setPage] = useState(1);

  const { data: account } = useQuery({
    queryKey: ['accounts', creditAnalysis?.accountId],
    queryFn: () => accountApi.findOne(creditAnalysis?.accountId!),
    enabled: !!creditAnalysis?.accountId,
  });

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['creditAnalysisHistory', account?.id, page],
    queryFn: () => creditAnalysisApi.getHistoryByAccount(account?.id || '', page, 10),
    enabled: !!account?.id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-gray-500">Carregando histórico...</p>
        </div>
      </div>
    );
  }

  if (!historyData || !account) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50/50">
        <CardContent className="flex flex-col items-center justify-center py-12 px-6">
          <div className="rounded-full bg-gray-100 p-4 mb-4">
            <Search className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nenhuma análise anterior encontrada
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-md mb-4">
            Esta conta ainda não possui histórico de análises de crédito. As análises aprovadas
            aparecerão aqui automaticamente após o processo de avaliação.
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <FileText className="h-3 w-3" />
            <span>O histórico é preenchido automaticamente após cada aprovação</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return <CreditAnalysisHistoryList historyData={historyData} page={page} onPageChange={setPage} />;
}
